import Button from "@/components/Button.tsx"
import DevErrorToast from "@/components/DevErrorToast"
import InputErrorMessage from "@/components/InputErrorMessage"
import { SelectInput, TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import {
  useAddPatientDeviceByKitMutation,
  useAddPatientDeviceMutation,
} from "@/data/queries"
import { HttpError } from "@/data/requests.ts"
import { deviceVendors } from "@/model/Devices"
import { addDeviceFormSchema } from "@/schemas/zod-schemas"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

type DeviceFormProps = {
  bloomlifeId: string
  onCancel: () => void
}

type DeviceIdentifierType = "imei" | "kit-id"

type DeviceFormData =
  | {
      identifierType: "imei"
      imei: string
    }
  | {
      identifierType: "kit-id"
      vendor: "impilo"
      kitId: string
    }

const identifierTypeOptions: {
  value: DeviceIdentifierType
  title: string
}[] = [
  { value: "imei", title: "IMEI" },
  { value: "kit-id", title: "Kit ID" },
]

const vendorOptions = deviceVendors.map(({ value, title }) => ({
  value,
  title,
  disabled: value !== "impilo",
}))

const DeviceForm = ({ bloomlifeId, onCancel }: DeviceFormProps) => {
  const { register, handleSubmit, watch, setValue, formState } =
    useForm<DeviceFormData>({
      mode: "onChange",
      resolver: zodResolver(addDeviceFormSchema),
      defaultValues: {
        identifierType: "imei",
        imei: "",
      },
    })
  const { errors } = formState
  const identifierType = watch("identifierType")

  useEffect(() => {
    if (identifierType === "kit-id") {
      setValue("vendor", "impilo")
    }
  }, [identifierType, setValue])

  const { mutateAsync: addDeviceByImei, isPending: isAddingByImei } =
    useAddPatientDeviceMutation(bloomlifeId)
  const { mutateAsync: addDeviceByKit, isPending: isAddingByKit } =
    useAddPatientDeviceByKitMutation(bloomlifeId)

  const isAddingDevice = isAddingByImei || isAddingByKit

  const onSubmit = async (formData: DeviceFormData) => {
    try {
      if (formData.identifierType === "imei") {
        await addDeviceByImei({
          bloomlifeId,
          imei: formData.imei,
        })
      } else {
        await addDeviceByKit({
          bloomlifeId,
          vendor: formData.vendor,
          kitId: formData.kitId,
        })
      }
      onCancel()
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Device added successfully." />
      ))
    } catch (error) {
      if (error instanceof HttpError && error.statusCode === 409) {
        toast.custom((toastId) => (
          <Toast
            toastId={toastId}
            description="Device with provided serial number already exists"
            variant="error"
          />
        ))
      } else {
        toast.custom(
          (toastId) => (
            <DevErrorToast
              userMessage="Something went wrong while adding the device. Try again, or contact the dev team."
              toastId={toastId}
              error={error}
            />
          ),
          { duration: Infinity }
        )
      }
    }
  }

  const isKitId = identifierType === "kit-id"
  const formErrors = errors as Partial<
    Record<"imei" | "vendor" | "kitId", { message?: string }>
  >

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="flex w-full gap-4">
        <div className="flex min-w-28 flex-col gap-1">
          <InputLabel htmlFor="identifierType" className="ml-1">
            Identifier
          </InputLabel>
          <SelectInput
            id="identifierType"
            options={identifierTypeOptions}
            disabled={isAddingDevice}
            {...register("identifierType")}
          />
        </div>

        {isKitId && (
          <div className="flex min-w-28 flex-col gap-1">
            <InputLabel htmlFor="vendor" className="ml-1">
              Vendor
            </InputLabel>
            <SelectInput
              id="vendor"
              options={vendorOptions}
              disabled={isAddingDevice}
              defaultValue="impilo"
              {...register("vendor")}
            />
            <InputErrorMessage
              message={formErrors?.vendor?.message}
              className="ml-1"
            />
          </div>
        )}

        <div className="flex w-full flex-col gap-1">
          <InputLabel htmlFor={isKitId ? "kitId" : "imei"} className="ml-1">
            {isKitId ? "Kit ID" : "IMEI"}
          </InputLabel>
          {isKitId ? (
            <>
              <TextInput
                id="kitId"
                placeholder="Kit ID"
                isError={!!formErrors?.kitId?.message}
                {...register("kitId")}
              />
              <InputErrorMessage
                message={formErrors?.kitId?.message}
                className="ml-1"
              />
            </>
          ) : (
            <>
              <TextInput
                id="imei"
                placeholder="IMEI"
                isError={!!formErrors?.imei?.message}
                {...register("imei")}
              />
              <InputErrorMessage
                message={formErrors?.imei?.message}
                className="ml-1"
              />
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-4">
        <Button
          type="button"
          variant="neutral"
          onClick={onCancel}
          disabled={isAddingDevice}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isAddingDevice}
          className="flex items-center gap-3 px-6"
        >
          {isAddingDevice && <Spinner className="size-4" />}
          Add device
        </Button>
      </div>
    </form>
  )
}

export default DeviceForm
